import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, Loader2, Pencil, Send, Trash2 } from 'lucide-react';
import { quoteService, type PhotoQuote, type PhotoQuoteStatus } from '@/services/quoteService';
import PageHeader from '@/components/shared/PageHeader';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const STATUS_STYLES: Record<PhotoQuoteStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  viewed: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  ordered: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  cancelled: 'bg-destructive/15 text-destructive',
};

const STATUS_LABELS: Record<PhotoQuoteStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Opened',
  ordered: 'Ordered',
  cancelled: 'Cancelled',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

/**
 * A sent quote: what staff put in front of the recipient (a customer, or a sales agent who may
 * be ordering for a customer), and what the recipient did with it.
 */
export default function QuoteDetail() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<PhotoQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  usePageTitle(quote ? `Photo Quote ${quote.quote_number}` : 'Photo Quote');

  const load = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    try {
      setQuote(await quoteService.getById(quoteId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load quote');
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSend() {
    if (!quote) return;
    setBusy(true);
    setError(null);
    try {
      const result = await quoteService.send(quote.id);
      setQuote(result.data);
      const to =
        quote.recipient_type === 'agent'
          ? quote.recipient_agent_email ?? 'the agent'
          : quote.customer_email ?? 'the customer';
      setNotice(
        result.email.success
          ? `Emailed to ${to}`
          : `Visible in the portal, but the email failed: ${result.email.error}`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send quote');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!quote) return;
    setBusy(true);
    try {
      await quoteService.remove(quote.id);
      navigate('/quotes');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete quote');
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!quote) {
    return <div className="p-6 text-sm text-muted-foreground">{error ?? 'Quote not found'}</div>;
  }

  const activeItems = quote.items.filter((item) => !item.removed);
  const removedItems = quote.items.filter((item) => item.removed);
  const total = activeItems.reduce(
    (sum, item) => sum + Number(item.unit_price) * (item.customer_quantity ?? item.quantity),
    0,
  );
  const amended = quote.items.some(
    (item) => item.removed || (item.customer_quantity != null && item.customer_quantity !== item.quantity),
  );
  const isAgent = quote.recipient_type === 'agent';
  const recipientWord = isAgent ? 'agent' : 'customer';
  const recipientName = isAgent
    ? quote.recipient_agent_name ?? quote.recipient_agent_id ?? '—'
    : quote.company_name ?? '—';

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader
          title={quote.quote_number}
          subtitle={quote.title ?? (isAgent ? `Agent: ${recipientName}` : recipientName)}
          breadcrumbs={[{ label: 'Photo Quotes', href: '/quotes' }, { label: quote.quote_number }]}
        />
        <div className="flex flex-wrap gap-2">
          {quote.status === 'draft' && (
            <Button intent="outline" onPress={() => navigate(`/quotes/${quote.id}/edit`)}>
              <Pencil className="size-4" />
              Edit
            </Button>
          )}
          {quote.status !== 'ordered' && quote.status !== 'cancelled' && (
            <Button onPress={handleSend} isDisabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              {quote.status === 'draft' ? `Send to ${recipientWord}` : 'Resend email'}
            </Button>
          )}
          {quote.status !== 'ordered' && (
            <Button intent="outline" onPress={handleDelete} isDisabled={busy}>
              <Trash2 className="size-4" />
              {quote.status === 'draft' ? 'Delete' : 'Cancel quote'}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground">{notice}</div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border p-4">
          <p className="text-xs uppercase text-muted-foreground">Status</p>
          <Badge variant="secondary" className={cn('mt-1 border-0', STATUS_STYLES[quote.status])}>
            {quote.status === 'viewed' ? `Opened by ${recipientWord}` : STATUS_LABELS[quote.status]}
          </Badge>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-xs uppercase text-muted-foreground">{isAgent ? 'Agent' : 'Customer'}</p>
          <p className="mt-1 text-sm font-medium">{recipientName}</p>
          <p className="text-xs text-muted-foreground">
            {isAgent
              ? [quote.recipient_agent_email, quote.company_name && `for ${quote.company_name}`]
                  .filter(Boolean)
                  .join(' · ') || '—'
              : quote.customer_email ?? '—'}
          </p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-xs uppercase text-muted-foreground">Sent / opened</p>
          <p className="mt-1 text-sm">{formatDateTime(quote.sent_at)}</p>
          <p className="text-xs text-muted-foreground">{formatDateTime(quote.first_viewed_at)}</p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-xs uppercase text-muted-foreground">Current value</p>
          <p className="mt-1 text-sm font-medium tabular-nums">{formatCurrency(total)}</p>
          {quote.valid_until && (
            <p className="text-xs text-muted-foreground">
              Prices held until{' '}
              {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(
                new Date(quote.valid_until),
              )}
            </p>
          )}
        </div>
      </div>

      {quote.message && (
        <div className="rounded-lg border bg-muted/30 p-4 text-sm">
          <p className="mb-1 text-xs uppercase text-muted-foreground">Note to {recipientWord}</p>
          {quote.message}
        </div>
      )}

      {amended && (
        <p className="text-sm text-muted-foreground">
          The {recipientWord} has changed this quote — amended quantities are shown against what you suggested.
        </p>
      )}

      <div className="rounded-lg border">
        <div className="border-b px-4 py-2.5 text-sm font-medium">
          Products ({activeItems.length})
        </div>
        <ul className="divide-y">
          {quote.items.map((item) => {
            const quantity = item.customer_quantity ?? item.quantity;
            const changed = item.customer_quantity != null && item.customer_quantity !== item.quantity;
            return (
              <li
                key={item.id}
                className={cn('flex gap-4 p-4', item.removed && 'opacity-50')}
              >
                <div className="size-16 shrink-0 overflow-hidden rounded bg-muted">
                  {item.image_url && (
                    <img src={item.image_url} alt="" className="size-full object-cover" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{item.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.sku}
                    {item.brand_name ? ` · ${item.brand_name}` : ''} · pack {item.pack_qty}
                  </p>
                  {item.removed && (
                    <p className="mt-1 text-xs text-destructive">Removed by the {recipientWord}</p>
                  )}
                </div>
                <div className="text-right text-sm">
                  <p className="tabular-nums">{formatCurrency(Number(item.unit_price))}</p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {changed ? (
                      <>
                        <span className="line-through">{item.quantity}</span> → {quantity}
                      </>
                    ) : (
                      <>× {quantity}</>
                    )}
                  </p>
                  {!item.removed && (
                    <p className="mt-1 font-medium tabular-nums">
                      {formatCurrency(Number(item.unit_price) * quantity)}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {removedItems.length > 0 && (
          <p className="border-t px-4 py-2 text-xs text-muted-foreground">
            {removedItems.length} {removedItems.length === 1 ? 'line' : 'lines'} removed by the {recipientWord}
          </p>
        )}
      </div>

      {quote.orders.length > 0 && (
        <div className="rounded-lg border">
          <div className="border-b px-4 py-2.5 text-sm font-medium">Orders placed from this quote</div>
          <ul className="divide-y">
            {quote.orders.map((order) => (
              <li key={order.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <div>
                  <p className="font-medium">{order.order_number ?? order.salesorder_number ?? '—'}</p>
                  <p className="text-xs text-muted-foreground">
                    {order.brand ?? '—'} · {formatDateTime(order.created_at)}
                  </p>
                </div>
                {order.order_id && (
                  <Button intent="plain" size="sm" onPress={() => navigate(`/order/${order.order_id}`)}>
                    View
                    <ExternalLink className="size-3.5" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
